import { useState, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportEditor } from "@/routes/incidents/ReportEditor";
import { useIncidentTypedReport } from "@/api/queries/reports";
import type { components } from "@/api/types";
import { formatDateTime } from "@/lib/format";
import {
  findSection,
  reportTypeLabel,
  sectionRows,
  SELECTABLE_REPORT_TYPES,
  type ReportType,
} from "@/lib/reports";

type GeneratedReport = components["schemas"]["GeneratedReportRead"];

export function TypedReportView({
  incidentId,
  tz,
}: {
  incidentId: string;
  tz?: string;
}) {
  const [type, setType] = useState<ReportType>("incident_summary");
  const { data, isLoading, isError } = useIncidentTypedReport(incidentId, type);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" role="tablist">
        {SELECTABLE_REPORT_TYPES.map((t) => (
          <Button
            key={t.value}
            size="sm"
            variant={t.value === type ? "default" : "outline"}
            aria-selected={t.value === type}
            role="tab"
            data-testid={`report-type-${t.value}`}
            onClick={() => setType(t.value)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : isError || !data ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Could not load the {reportTypeLabel(type)} report.
          </CardContent>
        </Card>
      ) : (
        <ReportBody report={data} tz={tz} incidentId={incidentId} />
      )}
    </div>
  );
}

function ReportBody({
  report,
  tz,
  incidentId,
}: {
  report: GeneratedReport;
  tz?: string;
  incidentId: string;
}) {
  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{report.title}</CardTitle>
          {report.is_authoritative ? (
            <Badge variant="success">Authoritative</Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {report.report_type === "incident_summary" ? (
          <IncidentSummarySections
            sections={report.sections}
            tz={tz}
            incidentId={incidentId}
          />
        ) : report.report_type === "exposure" ? (
          <ExposureSections sections={report.sections} />
        ) : report.report_type === "voice_record" ? (
          <VoiceRecordSections sections={report.sections} tz={tz} />
        ) : (
          <NarrativeSections
            sections={report.sections}
            tz={tz}
            incidentId={incidentId}
          />
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value == null || value === "" ? "—" : String(value)}</dd>
    </div>
  );
}

function IncidentSummarySections({
  sections,
  tz,
  incidentId,
}: {
  sections: GeneratedReport["sections"];
  tz?: string;
  incidentId: string;
}) {
  const head = findSection(sections, "Incident");
  const events = sectionRows<Record<string, unknown>>(
    findSection(sections, "Timeline"),
    "events",
  );

  // The IncidentEvent table is usually empty for a quick burn-test incident, so
  // the report's own "Timeline" section comes back with no rows. Synthesize the
  // dispatch/clear milestones from the incident header — mirroring how the
  // incident-detail page builds its timeline — so the panel always reflects the
  // real recorded times instead of a bare "No events recorded".
  const dispatchedAt = head?.dispatched_at as string | null | undefined;
  const clearedAt = head?.cleared_at as string | null | undefined;
  const timeline: { ts: string; label: string; sub?: string | null }[] = [];
  if (dispatchedAt) timeline.push({ ts: dispatchedAt, label: "Dispatched" });
  for (const e of events) {
    const ts = e.occurred_at as string | null;
    if (!ts) continue;
    timeline.push({
      ts,
      label: String(e.event_type ?? "Event"),
      sub: e.task ? String(e.task) : null,
    });
  }
  if (clearedAt) {
    timeline.push({
      ts: clearedAt,
      label: head?.status === "finalized" ? "Finalized" : "Cleared",
    });
  }
  timeline.sort((a, b) => a.ts.localeCompare(b.ts));

  return (
    <div className="space-y-6">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Incident #" value={head?.incident_number ?? incidentId} />
        <Field label="Type" value={head?.incident_type} />
        <Field label="Status" value={head?.status} />
        <Field
          label="Dispatched"
          value={formatDateTime(head?.dispatched_at as string | null, tz)}
        />
        <Field
          label="Cleared"
          value={formatDateTime(head?.cleared_at as string | null, tz)}
        />
        <Field label="Address" value={head?.address} />
      </dl>
      <div>
        <h4 className="mb-2 text-sm font-semibold">Timeline</h4>
        {timeline.length > 0 ? (
          <ol className="space-y-2 border-l border-border pl-4">
            {timeline.map((t, idx) => (
              <li key={idx}>
                <div className="text-xs text-muted-foreground">
                  {formatDateTime(t.ts, tz)}
                </div>
                <div className="text-sm font-medium">
                  {t.label}
                  {t.sub ? (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      · {t.sub}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">No events recorded.</p>
        )}
      </div>
    </div>
  );
}

function ExposureSections({
  sections,
}: {
  sections: GeneratedReport["sections"];
}) {
  const rows = sectionRows<Record<string, unknown>>(
    findSection(sections, "Per-firefighter exposure"),
    "firefighters",
  );
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No exposure records captured for this incident.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="py-1">Firefighter</th>
            <th className="py-1">CO peak</th>
            <th className="py-1">H2S peak</th>
            <th className="py-1">VOC peak</th>
            <th className="py-1">LEL peak</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className="border-t border-border">
              <td className="py-1 font-mono text-xs">
                {String(r.user_id ?? "—").slice(0, 8)}
              </td>
              <td className="py-1 tabular-nums">{ppm(r.co_ppm_peak)}</td>
              <td className="py-1 tabular-nums">{ppm(r.h2s_ppm_peak)}</td>
              <td className="py-1 tabular-nums">{ppm(r.voc_ppm_peak)}</td>
              <td className="py-1 tabular-nums">{pct(r.lel_pct_peak)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VoiceRecordSections({
  sections,
  tz,
}: {
  sections: GeneratedReport["sections"];
  tz?: string;
}) {
  const segments = sectionRows<Record<string, unknown>>(
    findSection(sections, "Audio segments"),
    "segments",
  );
  if (segments.length === 0) {
    return <p className="text-sm text-muted-foreground">No audio recorded.</p>;
  }
  return (
    <div className="space-y-3">
      {segments.map((s, idx) => (
        <div key={idx} className="rounded-md border border-border p-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{formatDateTime(s.started_at as string | null, tz)}</span>
            <Badge
              variant={s.transcript ? "secondary" : "outline"}
              className="text-[10px]"
            >
              {String(s.transcript_status ?? "unknown")}
            </Badge>
          </div>
          {s.transcript ? (
            <pre className="mt-2 whitespace-pre-wrap text-sm">
              {String(s.transcript)}
            </pre>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Not transcribed.
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function NarrativeSections({
  sections,
  tz,
  incidentId,
}: {
  sections: GeneratedReport["sections"];
  tz?: string;
  incidentId: string;
}) {
  const narrative = findSection(sections, "Narrative");
  const draft = narrative?.draft as Record<string, unknown> | null | undefined;
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Draft status" value={narrative?.status} />
        <Field
          label="Reviewed at"
          value={
            narrative?.reviewed_at
              ? formatDateTime(narrative.reviewed_at as string, tz)
              : "Not reviewed"
          }
        />
      </dl>
      {draft ? (
        <NarrativePreview draft={draft} tz={tz} />
      ) : (
        <p className="text-sm text-muted-foreground">
          No narrative draft generated yet.
        </p>
      )}
      <div className="border-t border-border pt-4">
        <h4 className="mb-2 text-sm font-semibold">Edit &amp; finalize draft</h4>
        <ReportEditor incidentId={incidentId} />
      </div>
    </div>
  );
}

// Render the after-action draft as a readable report preview rather than a raw
// JSON dump. The draft body is freeform (the editor guarantees the four core
// fields), so each section coerces defensively and falls back to an em-dash
// when a field is absent. Richer drafts (command decisions, resources, on-scene
// times) surface only when those keys are actually present.
function NarrativePreview({
  draft,
  tz,
}: {
  draft: Record<string, unknown>;
  tz?: string;
}) {
  const hazards = asStrings(draft.hazards_identified);
  const decon = asStrings(draft.decon_actions);
  const resources = asStrings(draft.resources_requested);
  const gaps = asStrings(draft.gaps);
  const decisions = asRows(draft.ic_decisions);
  const notes =
    typeof draft.after_action_notes === "string"
      ? draft.after_action_notes.trim()
      : "";
  const commenced = (draft.commencement_time ?? draft.commenced_at) as
    | string
    | null
    | undefined;
  const cleared = (draft.clear_time ?? draft.cleared_at) as
    | string
    | null
    | undefined;

  return (
    <div className="space-y-5 rounded-md border border-border bg-card p-4">
      {commenced || cleared ? (
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {commenced ? (
            <Field label="Commenced" value={formatDateTime(commenced, tz)} />
          ) : null}
          {cleared ? (
            <Field label="Cleared" value={formatDateTime(cleared, tz)} />
          ) : null}
        </dl>
      ) : null}

      <PreviewSection title="Hazards identified">
        <BulletList items={hazards} />
      </PreviewSection>

      <PreviewSection title="Decontamination actions">
        <BulletList items={decon} />
      </PreviewSection>

      {decisions.length > 0 ? (
        <PreviewSection title="Command decisions">
          <ul className="space-y-2">
            {decisions.map((d, idx) => (
              <li key={idx} className="text-sm">
                <div className="font-medium">
                  {d.decision ? String(d.decision) : "Decision"}
                  {d.time ? (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      · {formatDateTime(String(d.time), tz)}
                    </span>
                  ) : null}
                </div>
                {d.rationale ? (
                  <div className="text-muted-foreground">
                    {String(d.rationale)}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </PreviewSection>
      ) : null}

      {resources.length > 0 ? (
        <PreviewSection title="Resources requested">
          <BulletList items={resources} />
        </PreviewSection>
      ) : null}

      <PreviewSection title="After-action notes">
        {notes ? (
          <p className="whitespace-pre-wrap text-sm">{notes}</p>
        ) : (
          <Empty />
        )}
      </PreviewSection>

      <PreviewSection title="Open items to verify">
        <BulletList items={gaps} />
      </PreviewSection>
    </div>
  );
}

function PreviewSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-1">
      <h5 className="text-sm font-semibold">{title}</h5>
      {children}
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  if (items.length === 0) return <Empty />;
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm">
      {items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );
}

function Empty() {
  return <p className="text-sm text-muted-foreground">—</p>;
}

function asStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => (typeof v === "string" ? v.trim() : String(v ?? "")))
    .filter(Boolean);
}

function asRows(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (v): v is Record<string, unknown> =>
      typeof v === "object" && v !== null && !Array.isArray(v),
  );
}

function ppm(value: unknown): string {
  if (value == null || value === "") return "—";
  return `${value} ppm`;
}

function pct(value: unknown): string {
  if (value == null || value === "") return "—";
  return `${value}%`;
}
